import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockWatch, TimerClockFast } from 'shared/assets/icons';
import { Locales } from 'shared/assets/localization';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { durationOptions } from 'shared/constants';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { formatDurationLabel } from 'shared/utils/format-duration-label';

import { MetaInteraction } from '../meta-interaction/meta-interaction';

export type MetaDurationProps = {
  value?: number | null;
  onChange?: (duration?: number) => void;
};

export const MetaDuration: React.FC<MetaDurationProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const parsedValue = useMemo(
    () =>
      value ? formatDurationLabel(value, locale === Locales.NlNL) : undefined,
    [locale, value],
  );

  const _onChange = (time?: number) => {
    onChange?.(time);
    closeMenu();
  };

  const onClear = () => {
    onChange?.();
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={TimerClockFast}
        onClick={openMenu}
        placeholder={t('dialogs.task-detail.meta.duration.placeholder')}
        label={parsedValue}
        onClear={parsedValue ? onClear : undefined}
      />
      {menuOpen && (
        <TimePickerMenu
          referenceElement={buttonRef}
          value={value}
          timeFormat={TimeFormatOptions.Military}
          options={durationOptions}
          onChange={_onChange}
          onCancel={closeMenu}
          optionIcon={ClockWatch}
          placeholder={t('forms.task.fields.duration.placeholder')}
          clearLabel={t('task.timeslot.duration.options.clear.label')}
          hasCustomInput
          canClear
        />
      )}
    </>
  );
};
